import { useEffect, useState } from 'react';
import {
  Phone,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
  Bot,
  UserCheck,
  AlertTriangle,
  Loader2,
  Play,
} from 'lucide-react';
import { voiceService } from '@/services/voice.service';

/**
 * What a voice ticket actually shows an agent.
 *
 * Replaces a markdown blob that reached the sidebar as literal text — agents
 * read "## Voice Call Summary" and "**Caller:**" on screen, because the
 * description is rendered as plain text and nothing in the app renders
 * markdown. The same facts are already on the ticket as structured
 * `metadata.voiceCall`, so this reads those instead of parsing prose, and
 * needs no backend change.
 *
 * Session and call SIDs move behind a disclosure. They matter perhaps once a
 * month, when someone is chasing a call through the logs; they were occupying
 * the top of the panel on every ticket.
 *
 * Deliberately NOT the call summary. That belongs above the turns it
 * summarises, in the Conversation thread, and showing it in both places put
 * the same paragraph on screen twice.
 */

export interface VoiceCallMetadata {
  from?: string;
  to?: string;
  durationSeconds?: number;
  endReason?: string;
  messageCount?: number;
  callerCity?: string;
  callerCountry?: string;
  recordingUrl?: string;
  aiHandled?: boolean;
}

export interface VoiceCallView {
  call: VoiceCallMetadata;
  sessionId?: string;
  callSid?: string;
}

/**
 * Pull the voice details off a ticket, or null when it is not a voice ticket.
 *
 * Tolerant by design: metadata is `Record<string, unknown>` off the wire and a
 * ticket from an older producer may be missing any of it. A missing field
 * hides its row rather than rendering "undefined".
 */
export function readVoiceCall(
  metadata: Record<string, unknown> | undefined | null,
  source?: Record<string, unknown> | null,
): VoiceCallView | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const call = (metadata as any).voiceCall;
  if (!call || typeof call !== 'object') return null;

  return {
    call: call as VoiceCallMetadata,
    sessionId: typeof (source as any)?.sessionId === 'string' ? (source as any).sessionId : undefined,
    callSid: typeof (source as any)?.callSid === 'string' ? (source as any).callSid : undefined,
  };
}

/** "1m 43s", "43s", "—" — never a bare number of seconds. */
export function formatCallDuration(seconds?: number): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '—';
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`;
}

/**
 * How the call ended, in words an agent uses.
 *
 * `ai_resolved` is the raw value and it overstates things — it means the call
 * completed without a handoff, not that the customer's problem was solved.
 */
export function endReasonLabel(reason?: string): string {
  switch (reason) {
    case 'ai_resolved':
      return 'Handled by AI';
    case 'handoff':
      return 'Passed to an agent';
    case 'caller_hangup':
      return 'Caller hung up';
    case 'error':
      return 'Ended with an error';
    case 'after_hours':
      return 'Outside business hours';
    case 'voicemail':
      return 'Left a voicemail';
    default:
      return reason ? reason.replace(/_/g, ' ') : 'Unknown';
  }
}

function Chip({
  icon: Icon,
  children,
  tone = 'default',
}: {
  icon: typeof Clock;
  children: React.ReactNode;
  tone?: 'default' | 'warning';
}) {
  return (
    <span
      className={
        tone === 'warning'
          ? 'inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300'
          : 'inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-right text-sm text-gray-800 dark:text-gray-200 break-words">{value}</span>
    </div>
  );
}

/**
 * The call's recording, for a call whose audio we keep ourselves (Exotel).
 * Loaded only when asked for: a recording is megabytes, and most visits to a
 * ticket never play it. Fetched with the agent's session and played from
 * memory, so the browser is never sent to the phone provider.
 */
function TicketRecording({ ticketId }: { ticketId: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'none' | 'error'>('idle');
  const [src, setSrc] = useState<string | null>(null);

  // A different ticket is a different call.
  useEffect(() => {
    setState('idle');
    setSrc(null);
  }, [ticketId]);
  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  const load = async () => {
    setState('loading');
    try {
      setSrc(URL.createObjectURL(await voiceService.getTicketCallRecording(ticketId)));
      setState('ready');
    } catch (err: any) {
      setState(err?.response?.status === 404 ? 'none' : 'error');
    }
  };

  return (
    <div>
      <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Recording</p>
      {state === 'ready' && src ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio controls autoPlay src={src} className="w-full" />
      ) : state === 'none' ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No recording for this call.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={state === 'loading'}
            aria-busy={state === 'loading'}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {state === 'loading' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden />
            )}
            {state === 'loading' ? 'Loading recording…' : state === 'error' ? 'Try again' : 'Play recording'}
          </button>
          {state === 'error' && (
            <span role="alert" className="text-xs text-red-600 dark:text-red-400">
              The recording could not be loaded.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function VoiceCallPanel({ view, ticketId }: { view: VoiceCallView; ticketId?: string }) {
  const [open, setOpen] = useState(true);
  const [showIds, setShowIds] = useState(false);
  const { call, sessionId, callSid } = view;

  const location = [call.callerCity, call.callerCountry].filter(Boolean).join(', ');
  const endedBadly = call.endReason === 'error';
  const handedOff = call.endReason === 'handoff';

  return (
    <div className="border-b border-gray-100 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          <span>Call details</span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <Chip icon={Clock}>{formatCallDuration(call.durationSeconds)}</Chip>
            <Chip
              icon={endedBadly ? AlertTriangle : handedOff ? UserCheck : Bot}
              tone={endedBadly ? 'warning' : 'default'}
            >
              {endReasonLabel(call.endReason)}
            </Chip>
            {location && <Chip icon={MapPin}>{location}</Chip>}
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            <Row label="Caller" value={call.from} />
            <Row label="Called" value={call.to} />
            <Row
              label="Turns"
              value={typeof call.messageCount === 'number' ? call.messageCount : undefined}
            />
          </div>

          {call.recordingUrl ? (
            <div>
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Recording</p>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls preload="none" src={call.recordingUrl} className="w-full" />
            </div>
          ) : (
            ticketId && callSid && <TicketRecording ticketId={ticketId} />
          )}

          {(sessionId || callSid) && (
            <div>
              <button
                type="button"
                onClick={() => setShowIds(!showIds)}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showIds ? 'Hide' : 'Show'} technical details
              </button>
              {showIds && (
                <div className="mt-1 space-y-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">
                  {sessionId && <div className="break-all">Session {sessionId}</div>}
                  {callSid && <div className="break-all">Call {callSid}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
